/**
 * ZenMarkup Privacy Analytics — Events renderer.
 *
 * Renders auto-tracked and custom event widgets in the Behavior tab.
 * Extracted from insights.js to keep modules under 200 lines.
 */

import { setWidgetContent, emptyState } from './table-builder.js';

/** Items per page for the events widget. */
var EVENTS_PAGE_SIZE = 5;

/**
 * Map of auto-event prefixes to data-icon attribute names.
 * Order matters — first match wins.
 */
var AUTO_EVENT_ICONS = [
	{ prefix: 'outbound:', attr: 'data-icon-outbound' },
	{ prefix: 'download:', attr: 'data-icon-download' },
	{ prefix: 'mailto',    attr: 'data-icon-mailto'   },
	{ prefix: 'tel',       attr: 'data-icon-tel'      }
];

/**
 * Render both Events widgets (auto + custom) in Behavior tab.
 *
 * Shows/hides the section based on whether any events exist.
 *
 * @param {Object} data  Full stats response data.
 */
export function render(data) {
	var section = document.getElementById('zmpa-events-section');
	if (!section) {
		return;
	}

	var autoEvents   = data.events_auto   || [];
	var customEvents = data.events_custom || [];

	if (!autoEvents.length && !customEvents.length) {
		section.style.display = 'none';
		return;
	}

	section.style.display = '';

	renderWidget('events-auto', autoEvents, 'No auto-tracked events yet.');
	renderWidget('events-custom', customEvents, 'No custom events yet.');
}

/**
 * Render a single events widget (auto or custom).
 *
 * @param {string} widgetEl    The data-el value of the widget.
 * @param {Array}  events      Array of {name, total, unique}.
 * @param {string} emptyMsg    Message when no events.
 */
function renderWidget(widgetEl, events, emptyMsg) {
	if (!events.length) {
		setWidgetContent(widgetEl, emptyState(emptyMsg));
		return;
	}

	var widget = document.querySelector('[data-el="' + widgetEl + '"]');
	var icons  = {};
	if (widget) {
		AUTO_EVENT_ICONS.forEach(function (def) {
			icons[def.prefix] = widget.getAttribute(def.attr) || '';
		});
	}

	var wrap = document.createElement('div');
	wrap.className = 'zmpa-list';

	var header = document.createElement('div');
	header.className = 'zmpa-list-header';
	header.innerHTML = '<span>Event</span><span>Total</span><span>Unique</span>';
	wrap.appendChild(header);

	renderPage(wrap, events, 0, icons);

	setWidgetContent(widgetEl, wrap);
}

/**
 * Parse an auto-event name into { icon, label }.
 *
 * @param {string} name  Raw event name from backend.
 * @param {Object} icons Map of prefix → SVG HTML string.
 * @return {{ icon: string, label: string }}
 */
function parseEventName(name, icons) {
	for (var i = 0; i < AUTO_EVENT_ICONS.length; i++) {
		var prefix = AUTO_EVENT_ICONS[i].prefix;
		var svg    = icons[prefix] || '';
		if (name === prefix || name.indexOf(prefix + ' ') === 0) {
			var label = name.length > prefix.length
				? name.substring(prefix.length).replace(/^\s+/, '')
				: name;
			return { icon: svg, label: label };
		}
	}
	return { icon: '', label: name };
}

/**
 * Render a page of event rows into the list wrapper.
 *
 * @param {HTMLElement} wrap    The .zmpa-list container.
 * @param {Array}       events Full events array.
 * @param {number}      page   Current page (0-based).
 * @param {Object}      icons  Map of prefix → SVG HTML string.
 */
function renderPage(wrap, events, page, icons) {
	var old = wrap.querySelectorAll('.zmpa-list-row, .zmpa-pagination');
	old.forEach(function (el) { el.remove(); });

	var start = page * EVENTS_PAGE_SIZE;
	var pageEvents = events.slice(start, start + EVENTS_PAGE_SIZE);

	pageEvents.forEach(function (ev) {
		var row = document.createElement('div');
		row.className = 'zmpa-list-row';

		var parsed = parseEventName(ev.name, icons || {});

		var name = document.createElement('span');
		name.className = 'zmpa-list-name';
		name.title = ev.name;

		if (parsed.icon) {
			var iconSpan = document.createElement('span');
			iconSpan.className = 'zmpa-event-icon';
			iconSpan.innerHTML = parsed.icon;
			name.appendChild(iconSpan);
		}

		var textNode = document.createTextNode(parsed.label);
		name.appendChild(textNode);

		var total = document.createElement('span');
		total.className = 'zmpa-list-count';
		total.textContent = Number(ev.total).toLocaleString();

		var unique = document.createElement('span');
		unique.className = 'zmpa-list-count';
		unique.textContent = Number(ev.unique).toLocaleString();

		row.appendChild(name);
		row.appendChild(total);
		row.appendChild(unique);
		wrap.appendChild(row);
	});

	var totalPages = Math.ceil(events.length / EVENTS_PAGE_SIZE);
	if (totalPages > 1) {
		var content = document.querySelector('[data-el="content"]');
		var iconLeft  = content ? content.getAttribute('data-icon-chevron-left')  : '';
		var iconRight = content ? content.getAttribute('data-icon-chevron-right') : '';

		var nav = document.createElement('div');
		nav.className = 'zmpa-pagination';

		var prev = document.createElement('button');
		prev.className = 'zmpa-pagination-btn';
		prev.innerHTML = iconLeft + ' Prev';
		prev.disabled = page === 0;
		prev.addEventListener('click', function () {
			renderPage(wrap, events, page - 1, icons);
		});

		var info = document.createElement('span');
		info.className = 'zmpa-pagination-info';
		info.textContent = (page + 1) + ' / ' + totalPages;

		var next = document.createElement('button');
		next.className = 'zmpa-pagination-btn';
		next.innerHTML = 'Next ' + iconRight;
		next.disabled = page >= totalPages - 1;
		next.addEventListener('click', function () {
			renderPage(wrap, events, page + 1, icons);
		});

		nav.appendChild(prev);
		nav.appendChild(info);
		nav.appendChild(next);
		wrap.appendChild(nav);
	}
}
